import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import {
  useModals,
  Box,
  Button,
  ButtonLink,
  FontAwesomeIcon,
  LinkTo,
  Stack,
  Text,
  Title,
} from "@skailer-com/uikit";
import { Reactions } from "@/components/reactions/Reactions.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useMentionsHook } from "@/hooks/use-mention.hook.ts";
import { BlogPostComment } from "@/pages/blog/components/BlogPostComment.tsx";
import { BlogPostAttachments } from "@/pages/blog/components/BlogPostAttachments.tsx";
import { BlogPostBody } from "@/pages/blog/components/BlogPostBody.tsx";
import { BlogPostHeader } from "@/pages/blog/components/BlogPostHeader.tsx";
import { BlogPostViewersModal } from "@/pages/blog/index/widgets/BlogPostViewersModal.tsx";
import { useBlogAddReaction } from "@/queries/blog.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { ApiBlogPostShort, BlogPostMode, BlogPostModeType } from "@/types/blog.types.ts";
import { ApiEmployeeShort } from "@/types/employees/employee.types.ts";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

export interface BlogPostProps {
  mode?: BlogPostModeType;
  post: ApiBlogPostShort;
}

export interface BlogPostCommentMoreInfo {
  needShown: boolean;
  countMore: number;
  authorsMore: ApiEmployeeShort[];
}

export function BlogPost({ mode = "short", post }: BlogPostProps) {
  const { t } = useTranslation();
  const { mutateAsync: saveReaction } = useBlogAddReaction();
  const { renderDateAndTime } = useFormatter();
  const { channel_id } = useParams();
  const { replaceMentions } = useMentionsHook();
  const { openModal } = useModals();
  const channelId = channel_id ? Number(channel_id) : undefined;

  const commentMoreInfo = useMemo(() => {
    const result: BlogPostCommentMoreInfo = {
      needShown: false,
      countMore: 0,
      authorsMore: [],
    };

    if (mode !== "short") return result;
    if (post.comment_data && post.comment_data.length < 2) return result;

    result.needShown = true;
    result.countMore = post.total_comments ? post.total_comments - 1 : 0;

    const employeesForShow = post.comment_data?.reduce<Record<number, ApiEmployeeShort>>(
      (acc, comment, index) => {
        if (!index) return acc; // первый комментарий у нас скрывается

        acc[comment.author.id] = comment.author;

        return acc;
      },
      {} as Record<number, ApiEmployeeShort>
    );

    if (employeesForShow) {
      result.authorsMore = Object.values(employeesForShow).slice(0, 3);
    }

    return result;
  }, [post]);

  const save = useCallback(
    async (emoji: string, isNew: boolean) => {
      const { result } = await saveReaction({ id: post.id, emoji, isNew });

      analyticsService.trackEvent(
        isNew ? analyticEvents.blog.reactionAdded : analyticEvents.blog.reactionDeleted
      );

      return result.success;
    },
    [post]
  );

  const isScheduled = useMemo(() => {
    return post.datetime_published_at
      ? post.datetime_published_at.getTime() > new Date().getTime()
      : false;
  }, [post]);

  const content = useMemo(() => {
    return replaceMentions(post?.body, post?.mentioned_employees);
  }, [post]);

  const linkToPost = channelId
    ? `/news/${channelId}/${post.id}`
    : post.channels && post.channels.length > 0
      ? `/news/${post.channels[0].id}/${post.id}`
      : "/news";

  const openViewers = (postId: number) => {
    openModal(BlogPostViewersModal, { postId });
  };

  return (
    <Stack className={"bg-light rounded-sm shadow p-6"} gap={"md"}>
      {isScheduled && post.datetime_published_at && mode != "preview" && (
        <Stack direction={"horizontal"} gap={"sm"}>
          <Stack gap={"sm"} className={"py-1 px-2 bg-warning rounded-xs text-light"}>
            {t("blog:schedule_title", { time: renderDateAndTime(post.datetime_published_at) })}
          </Stack>
          {post?.acl?.can_edit && (
            <ButtonLink className={"shrink-0"} href={`/news/posts/${post.id}/edit`}>
              {t("common:edit")}
            </ButtonLink>
          )}
        </Stack>
      )}
      <Stack gap={"md"} className={isScheduled && mode != "preview" ? "opacity-20" : ""}>
        <BlogPostHeader post={post} />

        {mode == "short" ? (
          <LinkTo to={`${linkToPost}`} className={"no-underline"}>
            <Title size={"lg"} header={post.title} caption={post.caption}></Title>
          </LinkTo>
        ) : (
          <Title size={"lg"} header={post.title} caption={post.caption}></Title>
        )}

        {post.hero_image_file?.url && (
          <div className={"w-full bg-dark/5 rounded-xs overflow-hidden"}>
            <img src={post.hero_image_file?.url} className={"w-full block"} />
          </div>
        )}

        {mode == "short" ? (
          <Stack items={"start"}>
            <ButtonLink href={`${linkToPost}`}>{t("blog:read_more")}</ButtonLink>
          </Stack>
        ) : (
          <Box className={"break-word"}>
            <BlogPostBody
              postId={post.id}
              content={content ?? ""}
              isPreview={mode === BlogPostMode.preview}
            />
          </Box>
        )}

        {mode !== BlogPostMode.short && post.files && (
          <BlogPostAttachments postId={post.id} files={post.files} />
        )}

        <Stack justify={"between"} direction={"horizontal"} gap={"md"}>
          {mode != "preview" &&
            !isScheduled &&
            (post.reactions_disabled ? (
              <Text className={"text-secondary"}>{t("blog:reactions_disabled")}</Text>
            ) : (
              <Reactions reactions={post.reactions} save={save} />
            ))}

          <Button
            variant={"secondary"}
            className={"shrink-0"}
            disabled={post.views_count <= 0}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-eye" />}
            onClick={() => {
              openViewers(post.id);
            }}
          >
            {post.views_count}
          </Button>
        </Stack>

        {mode === BlogPostMode.short &&
          post.comment_data &&
          post.comment_data.length > 0 &&
          !isScheduled &&
          !post.comments_disabled && (
            <>
              <hr />

              {post.comment_data.slice(0, 1).map((comment, index) => (
                <BlogPostComment
                  comment={comment}
                  key={`comment-${comment.entity_id}-${index}`}
                  postId={post.id}
                  mode={mode}
                />
              ))}

              {commentMoreInfo.needShown && (
                <Stack direction={"horizontal"} gap={"sm"} items={"center"}>
                  {commentMoreInfo.authorsMore.length && (
                    <Stack direction={"horizontal"} gap={"xs"}>
                      {commentMoreInfo.authorsMore.map((author) => (
                        <EmployeeAvatar
                          employee={author}
                          size={"sm"}
                          key={`comment-author-${author.id}`}
                        />
                      ))}
                    </Stack>
                  )}
                  <LinkTo
                    to={`${linkToPost}`}
                    className={"no-underline text-dark font-semibold text-sm"}
                  >
                    {t("plurals:more_comments", { count: commentMoreInfo.countMore })}
                  </LinkTo>
                </Stack>
              )}
            </>
          )}
      </Stack>
    </Stack>
  );
}
